/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu May 02 2024
 **/

import { useEffect } from "react";

import { useModal } from "@/frameworks/designStuff/components";
import { useChatDataStore } from "@/modules/chat/store";
import { EventHandler } from "@rfice/eventhandler";

import { usePopover } from "../usePopover";
import { useMiniChatPopupManager } from "../useMiniChatPopupManager";
import { useFullSizeChatManager } from "../useFullSizeChatManager";
import { usePersistActions } from "../../persistence";
import { useSidebarActions } from "../useSidebar";
import { SidebarID } from "../../commons";

export default function useEscape() {
	const popoverActions = usePopover();
	const { miniChatPopupActions } = useMiniChatPopupManager();
	const { fullSizeChatActions } = useFullSizeChatManager();
	const modalActions = useModal();
	const { chats } = usePersistActions();
	const sidebarActions = useSidebarActions();

	const closePopover = () => {
		const popoverList = popoverActions.getAll();
		const lastPopover = [...popoverList].pop();
		if (!lastPopover) return;
		if ("uniqueID" in lastPopover) {
			popoverActions.hide(lastPopover.popoverID, lastPopover.uniqueID);
		} else {
			popoverActions.hide(lastPopover.popoverID);
		}
	};

	const closeChat = () => {
		if (useChatDataStore.getState().show_fullsize_chat) {
			const fullSizeChat = fullSizeChatActions.get();
			if (!fullSizeChat) return;
			// 비공개 스페이스의 비공개 방에서는 isUpdating 가 없을 수 있어서 옵셔널로 변경
			if (useChatDataStore.getState().room_ui[fullSizeChat.channelID]?.isUpdating) {
				return;
			}
			EventHandler.fire("$Editor::Draft$", fullSizeChat.channelID);
			const draft = chats.chat_draft(fullSizeChat.channelID);
			if (draft) {
				chats.check_has_draft(fullSizeChat.channelID, true);
			}
			fullSizeChatActions.hide();
		} else {
			const currentFocusChat = miniChatPopupActions.getCurrentFocus();
			if (!currentFocusChat) return;
			// 비공개 스페이스의 비공개 방에서는 isUpdating 가 없을 수 있어서 옵셔널로 변경
			if (useChatDataStore.getState().room_ui[currentFocusChat.id]?.isUpdating) {
				return;
			}
			EventHandler.fire("$Editor::Draft$", currentFocusChat.id);
			const draft = chats.chat_draft(currentFocusChat.id);
			if (draft) {
				chats.check_has_draft(currentFocusChat.id, true);
			}
			miniChatPopupActions.hide(currentFocusChat.id);
		}
	};

	const closeSidebar = () => {
		const sidebarList = [
			SidebarID.Chat,
			SidebarID.Meeting,
			SidebarID.People,
			SidebarID.Phone,
			SidebarID.Space,
			SidebarID.Activity
		] as SidebarID[];

		if (sidebarList.includes(sidebarActions.getFocus()!)) {
			sidebarActions.hideAll("left");
		} else {
			sidebarActions.hide(sidebarActions.getFocus() as SidebarID);
		}
	};

	const handleEscape = (ev: KeyboardEvent) => {
		if (ev.key !== "Escape") return;
		const lastModal = modalActions.getLast();

		if (lastModal && !lastModal.preventEsc) {
			modalActions.hide(lastModal.id);
			return;
		}

		if (popoverActions.getAll().length > 0) {
			closePopover();
			return;
		}

		if (sidebarActions.getFocus()) {
			closeSidebar();
			return;
		}

		closeChat();
	};

	useEffect(() => {
		window.addEventListener("keydown", handleEscape);
		return () => {
			window.removeEventListener("keydown", handleEscape);
		};
	}, []);
}
